import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePrestadores } from '@/hooks/usePrestadores';

export type OrdensPeriodo = 'hoje' | 'semana' | 'mes' | 'tudo';

export interface OrdensFiltrosState {
  prestadorId?: string;  // undefined = todos; '__none__' = sem prestador
  periodo: OrdensPeriodo;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ORDENS_FILTROS_INICIAL: OrdensFiltrosState = {
  periodo: 'tudo',
};

interface Props {
  filtros: OrdensFiltrosState;
  onChange: (next: OrdensFiltrosState) => void;
}

/**
 * Painel de filtros avançados para /ordens — renderiza o conteúdo do popover.
 * O parent (OrdensServico page) envolve num Popover e dispara via "Filtros" btn.
 */
export function OrdensFiltros({ filtros, onChange }: Props) {
  const { t } = useTranslation();
  const { data: prestadores } = usePrestadores();

  const temFiltrosAtivos = filtros.prestadorId !== undefined || filtros.periodo !== 'tudo';

  const limpar = () => onChange(ORDENS_FILTROS_INICIAL);

  const periodos: { value: OrdensPeriodo; label: string }[] = [
    { value: 'hoje', label: t('common.today', 'Hoje') },
    { value: 'semana', label: t('common.thisWeek', 'Esta semana') },
    { value: 'mes', label: t('common.thisMonth', 'Este mês') },
    { value: 'tudo', label: t('common.all', 'Tudo') },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm">{t('common.filters')}</h4>
        {temFiltrosAtivos && (
          <Button variant="ghost" size="sm" onClick={limpar}>
            <X className="mr-1 h-3 w-3" />
            {t('common.clear', 'Limpar')}
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('orders.form.provider')}
        </Label>
        <Select
          value={filtros.prestadorId ?? '__all__'}
          onValueChange={(v) =>
            onChange({
              ...filtros,
              prestadorId: v === '__all__' ? undefined : v,
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('common.all', 'Todos')}</SelectItem>
            <SelectItem value="__none__">{t('orders.form.notAssigned')}</SelectItem>
            {prestadores?.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.profile?.nome || t('common.noName')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('common.period', 'Período')}
        </Label>
        <div className="flex flex-wrap gap-2">
          {periodos.map((p) => (
            <Button
              key={p.value}
              variant={filtros.periodo === p.value ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() => onChange({ ...filtros, periodo: p.value })}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
